import { DEVICE_ID_STORAGE_KEY } from '../assets/constants'
import type { LoginRequest } from '../proto/generated/identity'
import { LoginRequest as LoginRequestCodec } from '../proto/generated/identity'

function randomUuid(): string {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID()
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0
		const v = c === 'x' ? r : (r & 0x3) | 0x8
		return v.toString(16)
	})
}

/** Stable per-browser device id for session / refresh APIs. */
export function getDeviceId(): string {
	let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY)
	if (!id) {
		id = randomUuid()
		localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
	}
	return id
}

/** First three numeric segments (e.g. 146.0.6490.142 → 146.0.6490). */
function versionHead3(version: string): string {
	const parts = version.split('.').filter((p) => p !== '')
	if (parts.length <= 3) return version
	return `${parts[0]}.${parts[1]}.${parts[2]}`
}

function parseBrowserWithVersion(ua: string): string {
	const edge = ua.match(/Edg(?:e)?\/(\d+\.\d+\.\d+)/i)
	if (edge) {
		return `Edge ${versionHead3(edge[1])}`
	}
	const chrome = ua.match(/Chrome\/(\d+\.\d+\.\d+)/)
	if (chrome && !/Edg/i.test(ua)) {
		return `Chrome ${versionHead3(chrome[1])}`
	}
	const ff = ua.match(/Firefox\/(\d+\.\d+)/)
	if (ff) {
		return `Firefox ${ff[1]}`
	}
	const safari = ua.match(/Version\/(\d+(?:\.\d+)*)\s+Safari\//)
	if (safari && !/Chrome/i.test(ua)) {
		return `Safari ${versionHead3(safari[1])}`
	}
	return 'Browser'
}

function parseOsWithVersion(ua: string): string {
	const mac = ua.match(/Mac OS X (\d+)[._](\d+)[._](\d+)/i)
	if (mac) {
		return `Mac OS X ${mac[1]}.${mac[2]}.${mac[3]}`
	}
	const macShort = ua.match(/Mac OS X (\d+)[._](\d+)\b/i)
	if (macShort) {
		return `Mac OS X ${macShort[1]}.${macShort[2]}`
	}
	const win = ua.match(/Windows NT (\d+\.\d+)/i)
	if (win) {
		return `Windows NT ${win[1]}`
	}
	if (/Android\s+(\d+(?:\.\d+)*)/i.test(ua)) {
		const m = ua.match(/Android\s+(\d+(?:\.\d+)*)/i)
		return m ? `Android ${m[1]}` : 'Android'
	}
	if (/iPhone OS (\d+)[._](\d+)/i.test(ua)) {
		const m = ua.match(/iPhone OS (\d+)[._](\d+)/i)
		return m ? `iOS ${m[1]}.${m[2]}` : 'iOS'
	}
	if (/Linux/i.test(ua)) {
		return 'Linux'
	}
	return 'Unknown OS'
}


function buildDeviceLabel(): string {
	const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
	const browser = parseBrowserWithVersion(ua)
	const os = parseOsWithVersion(ua)
	return `${browser}, ${os}`
}

export async function buildDeviceInfoSummary(): Promise<string> {
	return buildDeviceLabel()
}

export async function buildEmailPasswordLoginRequest(
	email: string,
	password: string,
): Promise<LoginRequest> {
	return LoginRequestCodec.fromPartial({
		email,
		password,
		deviceId: getDeviceId(),
		deviceInfo: await buildDeviceInfoSummary(),
	})
}
